import React from 'react';
import { Container } from 'react-bootstrap';
import GradientHero from '../shared/GradientHero';
import '../common-styles.css';
import './BusinessPrinciplesPage.css';

const BusinessPrinciplesPage: React.FC = () => {
  return (
    <div className="bp-page">
      <GradientHero
        backTo="/"
        title={<span>Ethical Business<br/>Conduct Principles</span>}
        className="bp-hero"
        titleClassName="bp-hero-title"
      />

      <section className="page-frame bp-content" aria-label="Ethical Business Conduct Principles">
        <Container>
          {/* 1. Prevention of Financial Crimes */}
          <article className="bp-block" aria-labelledby="bp-h1">
            <h2 id="bp-h1" className="bp-heading">1. Prevention of Financial Crimes</h2>
            <p>
              Our company acknowledges the risk exposure of the gems and jewellery industry to money
              laundering, terrorist financing, and financial crimes. We are committed to identifying and
              addressing these risks through robust internal systems.
            </p>
            <p>We maintain full adherence to all applicable national and international regulations related to:</p>
            <ul>
              <li>Money laundering</li>
              <li>Terrorism financing</li>
              <li>Bribery and corruption</li>
              <li>Fraud, tax evasion, and financial misconduct</li>
              <li>Transfer pricing, embezzlement, and smuggling</li>
            </ul>
            <p>
              Our financial activities are subject to regular audits, and internal controls are in place to ensure
              compliance.
            </p>
            <p>
              All employees are expected to understand and follow relevant legal and internal requirements. Failure
              to report suspicious activity may constitute a serious breach of our Code of Conduct.
            </p>
            <p>Our compliance team oversees:</p>
            <ul>
              <li>Customer and supplier due diligence (KYC/KYS)</li>
              <li>Monitoring and identifying unusual transactions</li>
              <li>Reporting obligations</li>
              <li>Record-keeping, as per regulatory standards</li>
            </ul>
          </article>

          {/* 2. Conflict-Free Diamond Sourcing */}
          <article className="bp-block" aria-labelledby="bp-h2">
            <h2 id="bp-h2" className="bp-heading">2. Conflict-Free Diamond Sourcing</h2>
            <p>
              Our organization fully supports and complies with the Kimberley Process Certification Scheme and the
              World Diamond Council’s System of Warranties (WDC SoW).
            </p>
            <p>
              We ensure our diamonds are sourced from legitimate suppliers who are not involved in financing conflict.
              All sales documentation includes the WDC recommended declaration guaranteeing the conflict-free status of
              our diamonds.
            </p>
          </article>

          {/* 3. Anti-Bribery and Corruption */}
          <article className="bp-block" aria-labelledby="bp-h3">
            <h2 id="bp-h3" className="bp-heading">3. Anti-Bribery and Corruption</h2>
            <p>
              We have zero tolerance for bribery, facilitation payments, or any form of unethical inducement.
            </p>
            <p>We do not permit:</p>
            <ul>
              <li>Accepting or offering cash or gifts that may influence business decisions</li>
              <li>Compromising fair trade through illicit payments</li>
            </ul>
            <p>
              All actions must align with local anti-corruption laws, and our staff are trained to recognize and report
              any violation.
            </p>
          </article>

          {/* 4. Product Integrity */}
          <article className="bp-block" aria-labelledby="bp-h4">
            <h2 id="bp-h4" className="bp-heading">4. Product Integrity: Disclosure of Diamond Treatments</h2>
            <p>
              Transparency is central to our business. In all transactions involving treated, synthetic, or imitation
              stones, full disclosure is mandatory.
            </p>
            <p>Key points include:</p>
            <ul>
              <li>Accurate and honest representation of stones</li>
              <li>No misuse of terms like “diamond” for synthetics or simulants</li>
              <li>Providing clear information on invoices and during customer interactions</li>
            </ul>
            <p>Definitions we adopt:</p>
            <ul>
              <li><strong>Natural Diamond:</strong> A carbon-based mineral crystallized naturally.</li>
              <li><strong>Synthetic:</strong> Lab-created stones matching diamond structure but not of natural origin.</li>
              <li><strong>Treated Diamond:</strong> Naturally occurring or synthetic stones enhanced through treatments not including traditional cutting/polishing.</li>
              <li><strong>Simulants:</strong> Materials resembling diamonds but not meeting diamond specifications (e.g., CZ).</li>
            </ul>
          </article>

          {/* 5. Conflict Minerals Policy */}
          <article className="bp-block" aria-labelledby="bp-h5">
            <h2 id="bp-h5" className="bp-heading">5. Conflict Minerals Policy</h2>
            <p>We stand against human rights abuses linked to conflict minerals.</p>
            <p>We ensure:</p>
            <ul>
              <li>None of our products originate from high-risk or conflict-affected areas (CAHRAs)</li>
              <li>All suppliers are informed of our sourcing expectations</li>
              <li>Ethical sourcing compliance is monitored and annually reported</li>
            </ul>
            <p>
              Customers may request our Ethical Sourcing Compliance Report via a formal written request.
            </p>
          </article>

          {/* 6. Responsible Supply Chain Practices */}
          <article className="bp-block" aria-labelledby="bp-h6">
            <h2 id="bp-h6" className="bp-heading">6. Responsible Supply Chain Practices</h2>
            <p>
              We actively encourage and support our suppliers and partners, including Tier 2 and Tier 3 vendors, to adopt
              ethical and legal business practices aligned with our standards.
            </p>
          </article>

          {/* 7. Labour Standards and Employee Rights */}
          <article className="bp-block" aria-labelledby="bp-h7">
            <h2 id="bp-h7" className="bp-heading">7. Labour Standards and Employee Rights</h2>
            <h3 className="bp-subheading">Working Hours &amp; Compensation</h3>
            <ul>
              <li>We follow all national labour laws on working hours and overtime</li>
              <li>Employees receive at least minimum wages and basic living allowances</li>
            </ul>

            <h3 className="bp-subheading">Non-Discrimination &amp; Fair Treatment</h3>
            <ul>
              <li>No bias based on race, gender, caste, religion, disability, or other factors</li>
              <li>No physical or psychological coercion, corporal punishment, or harassment is tolerated</li>
            </ul>

            <h3 className="bp-subheading">Child Labour</h3>
            <ul>
              <li>No one under 15 years (or higher legal age, if applicable) is employed</li>
              <li>Age verification processes are in place</li>
              <li>For legal young workers (15–17), conditions must meet regulatory safeguards</li>
            </ul>

            <h3 className="bp-subheading">Forced Labour</h3>
            <ul>
              <li>We reject all forms of involuntary labour</li>
              <li>Employment is offered freely and lawfully</li>
            </ul>

            <h3 className="bp-subheading">Human Rights</h3>
            <ul>
              <li>All employees are treated with dignity and fairness</li>
              <li>The company respects cultural and political freedoms, union membership, and religious beliefs</li>
              <li>Any form of abuse or harassment is strictly prohibited</li>
            </ul>
          </article>

          {/* 8. Environmental Commitment */}
          <article className="bp-block" aria-labelledby="bp-h8">
            <h2 id="bp-h8" className="bp-heading">8. Environmental Commitment</h2>
            <p>
              We are dedicated to minimizing our environmental footprint. Our sustainability initiatives include:
            </p>
            <ul>
              <li>Full compliance with environmental regulations</li>
              <li>Continuous review of our operational impact</li>
              <li>Employee education and training on environmental practices</li>
              <li>Monitoring performance through audits and accountability systems</li>
            </ul>
          </article>

          {/* 9. Product Security */}
          <article className="bp-block" aria-labelledby="bp-h9">
            <h2 id="bp-h9" className="bp-heading">9. Product Security</h2>
            <p>
              We implement stringent measures across our value chain to ensure product safety:
            </p>
            <ul>
              <li>Insurance coverage at every stage of processing</li>
              <li>Secure storage and surveillance at all facilities</li>
              <li>Trained personnel and emergency response protocols</li>
              <li>Rigorous quality control to prevent product tampering or misplacement</li>
            </ul>
          </article>

          {/* 10. Public Grievance Redressal */}
          <article className="bp-block" aria-labelledby="bp-h10">
            <h2 id="bp-h10" className="bp-heading">10. Public Grievance Redressal</h2>
            <p>
              To uphold public trust, our company has instituted a dedicated grievance mechanism.
            </p>
            <ul>
              <li>
                Any external concerns, policy violations, or ethical red flags can be submitted to our Public Grievance
                Cell.
              </li>
              <li>
                Queries related to products or practices will receive a formal response within 5 working days, following
                our standard review process.
              </li>
            </ul>
          </article>
        </Container>
      </section>
    </div>
  );
};

export default BusinessPrinciplesPage;
